import { useEffect, useMemo, useState } from "react";
import type { CadencePeriod, CadenceTask } from "../../api/cadence.ts";
import type { Lead } from "../../types.ts";
import { quickCadenceDate, type QuickCadencePreset } from "../CadenceCalendar.helpers.ts";
import { LeadPicker } from "./LeadPicker.tsx";

export type CadenceScheduleSubmit = {
  leadIds: string[];
  dates: string[];
  startDate?: string;
  endDate?: string;
  periods: CadencePeriod[];
  note: string;
};

type ScheduleDialogProps = {
  open: boolean;
  mode: "create" | "reschedule";
  leads: Lead[];
  selectedDayTasks: CadenceTask[];
  initialLeadIds: string[];
  initialDate: string;
  initialPeriod?: CadencePeriod;
  initialNote?: string;
  saving: boolean;
  onClose: () => void;
  onSubmit: (payload: CadenceScheduleSubmit) => void;
};

const PERIOD_OPTIONS: Array<{ value: CadencePeriod; label: string }> = [
  { value: "", label: "Qualquer" },
  { value: "manha", label: "Manha" },
  { value: "tarde", label: "Tarde" },
  { value: "noite", label: "Noite" },
];

const QUICK_OPTIONS: Array<{ value: QuickCadencePreset; label: string }> = [
  { value: "tomorrow", label: "Amanha" },
  { value: "in3", label: "Em 3 dias" },
  { value: "in7", label: "Em 7 dias" },
  { value: "nextMonday", label: "Proxima segunda" },
];

export function ScheduleDialog({
  open,
  mode,
  leads,
  selectedDayTasks,
  initialLeadIds,
  initialDate,
  initialPeriod = "",
  initialNote = "",
  saving,
  onClose,
  onSubmit,
}: ScheduleDialogProps) {
  const [leadQuery, setLeadQuery] = useState("");
  const [selectedLeadIds, setSelectedLeadIds] = useState<string[]>([]);
  const [dateInput, setDateInput] = useState(initialDate);
  const [selectedDates, setSelectedDates] = useState<string[]>([initialDate]);
  const [rangeEnd, setRangeEnd] = useState("");
  const [periods, setPeriods] = useState<CadencePeriod[]>([initialPeriod]);
  const [note, setNote] = useState(initialNote);

  useEffect(() => {
    if (!open) return;
    setLeadQuery("");
    setSelectedLeadIds(initialLeadIds);
    setDateInput(initialDate);
    setSelectedDates([initialDate]);
    setRangeEnd("");
    setPeriods([initialPeriod]);
    setNote(initialNote);
  }, [initialDate, initialLeadIds, initialNote, initialPeriod, open]);

  const dateSummary = useMemo(() => {
    if (rangeEnd && rangeEnd !== initialDate) return `${initialDate} ate ${rangeEnd}`;
    return selectedDates.join(", ");
  }, [initialDate, rangeEnd, selectedDates]);

  if (!open) return null;

  function toggleLead(leadId: string) {
    if (mode === "reschedule") return;
    setSelectedLeadIds((current) => (
      current.includes(leadId) ? current.filter((id) => id !== leadId) : [...current, leadId]
    ));
  }

  function togglePeriod(period: CadencePeriod) {
    setPeriods((current) => {
      if (mode === "reschedule") return [period];
      const next = current.includes(period) ? current.filter((item) => item !== period) : [...current, period];
      return next.length ? next : [""];
    });
  }

  function addDate(date: string) {
    if (!date) return;
    if (mode === "reschedule") {
      setSelectedDates([date]);
      setDateInput(date);
      return;
    }
    setSelectedDates((current) => Array.from(new Set([...current, date])).sort());
    setDateInput(date);
  }

  function removeDate(date: string) {
    setSelectedDates((current) => {
      const next = current.filter((item) => item !== date);
      return next.length ? next : [initialDate];
    });
  }

  function addQuickDate(preset: QuickCadencePreset) {
    addDate(quickCadenceDate(preset, new Date(`${initialDate}T00:00:00`)));
  }

  function submit() {
    onSubmit({
      leadIds: selectedLeadIds,
      dates: selectedDates,
      startDate: rangeEnd && rangeEnd !== initialDate ? initialDate : undefined,
      endDate: rangeEnd && rangeEnd !== initialDate ? rangeEnd : undefined,
      periods,
      note,
    });
  }

  return (
    <div className="cadence-dialog-backdrop">
      <section className="cadence-dialog" role="dialog" aria-modal="true" aria-label="Agendar cadencia">
        <div className="cadence-dialog-head">
          <div>
            <span className="cadence-eyebrow">{mode === "reschedule" ? "Reagendar" : "Novo agendamento"}</span>
            <h2>{mode === "reschedule" ? "Reagendar cadencia" : "Agendar cadencia"}</h2>
          </div>
          <button type="button" onClick={onClose}>Fechar</button>
        </div>

        <div className="cadence-dialog-body">
          <div className="cadence-form-row">
            <label htmlFor="cadence-date-input">Data</label>
            <div className="cadence-date-add">
              <input
                id="cadence-date-input"
                className="cadence-input"
                type="date"
                value={dateInput}
                onChange={(event) => setDateInput(event.currentTarget.value)}
              />
              <button type="button" onClick={() => addDate(dateInput)}>Adicionar</button>
            </div>
          </div>

          {mode === "create" ? (
            <div className="cadence-quick-dates" aria-label="Datas rapidas">
              {QUICK_OPTIONS.map((option) => (
                <button key={option.value} type="button" onClick={() => addQuickDate(option.value)}>
                  {option.label}
                </button>
              ))}
            </div>
          ) : null}

          <div className="cadence-selected-dates" aria-label="Datas selecionadas">
            {selectedDates.map((date) => (
              <span key={date}>
                {date}
                {mode === "create" ? <button type="button" onClick={() => removeDate(date)}>x</button> : null}
              </span>
            ))}
          </div>

          {mode === "create" ? (
            <div className="cadence-form-row">
              <label htmlFor="cadence-range-end-dialog">Periodo final</label>
              <input
                id="cadence-range-end-dialog"
                className="cadence-input"
                type="date"
                value={rangeEnd}
                min={initialDate}
                onChange={(event) => setRangeEnd(event.currentTarget.value)}
              />
            </div>
          ) : null}

          <fieldset className="cadence-periods">
            <legend>Periodos</legend>
            {PERIOD_OPTIONS.map((option) => (
              <label key={option.value || "any"} className={periods.includes(option.value) ? "is-selected" : ""}>
                <input
                  type="checkbox"
                  checked={periods.includes(option.value)}
                  onChange={() => togglePeriod(option.value)}
                />
                {option.label}
              </label>
            ))}
          </fieldset>

          <LeadPicker
            leads={leads}
            tasks={selectedDayTasks}
            query={leadQuery}
            selectedLeadIds={selectedLeadIds}
            onQueryChange={setLeadQuery}
            onToggleLead={toggleLead}
          />

          <label className="cadence-note">
            Nota
            <textarea value={note} onChange={(event) => setNote(event.currentTarget.value)} placeholder="Ex: Enviar proposta e confirmar duvidas" />
          </label>

          <div className="cadence-dialog-foot">
            <span>{dateSummary || "Nenhuma data selecionada"}</span>
            <button className="cadence-primary" type="button" disabled={saving} onClick={submit}>
              {saving ? "Salvando..." : mode === "reschedule" ? "Reagendar" : "Agendar cadencia"}
            </button>
          </div>
        </div>
      </section>
    </div>
  );
}
